import React from 'react';
import { Typography, Box, Divider, Paper, Card, CardContent, Link } from '@mui/material';
import { Search, TrendingUp, InfoOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../components/layout/PageContainer';
import DisclaimerModal from '../components/common/DisclaimerModal';
import useDisclaimer from '../hooks/useDisclaimer';

const AboutUs: React.FC = () => {
    const { isOpen: disclaimerOpen, openDisclaimer, closeDisclaimer } = useDisclaimer();
    const navigate = useNavigate();

    // Feature flag for Financial Data
    const isFinancialDataEnabled = process.env.REACT_APP_ENABLE_FINANCIAL_DATA === 'true';

    const handleEntitySearchClick = () => {
        navigate('/search?tab=0');
    };

    const handleFinancialDataClick = () => {
        if (isFinancialDataEnabled) {
            navigate('/search?tab=1');
        }
    };

    return (
        <PageContainer>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 400, mb: 3 }}>
                        Welcome to Ethics VC
                    </Typography>
                    <Paper elevation={1} sx={{ p: 4, backgroundColor: 'grey.50' }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                            The Institute for Ethical Venture Capital (ethics.vc) is a non-profit which researches best practices in ethical Venture Capital, focusing on the unique opportunities for world changing innovation to be pointed in the direction of good. This application lets you explore the venture capital investments made by institutions—such as university endowments—allowing you to dig deeper into the venture capital firms, organizations, and individuals involved. Additional features, including complicity review, data verification and evidence submission, are coming soon.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                            Please read our{' '}
                            <Link 
                                component="button"
                                variant="body2"
                                onClick={openDisclaimer}
                                sx={{ 
                                    color: 'info.main',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        color: 'info.dark'
                                    }
                                }}
                            >
                                disclaimer
                            </Link>
                            {' '}for important information about data verification and platform status.
                        </Typography>
                    </Paper>
                </Box>

                {/* Search Tools Overview */}
                <Box sx={{ mb: 6 }}>
                    <Typography variant="h5" sx={{ fontWeight: 400, mb: 4, textAlign: 'center' }}>
                        {isFinancialDataEnabled
                            ? 'Two powerful tools to support your research include:'
                            : 'Our research tool to support your work:'}
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: isFinancialDataEnabled ? 'repeat(2, 1fr)' : '1fr' }, gap: 3 }}>
                        <Card 
                            elevation={2} 
                            sx={{ 
                                p: 3, 
                                cursor: 'pointer',
                                transition: 'box-shadow 0.2s ease-in-out',
                                '&:hover': {
                                    boxShadow: 4
                                }
                            }}
                            onClick={handleEntitySearchClick}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Search sx={{ color: 'primary.main', mr: 2 }} />
                                <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                    Entity Search:
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                Investigate people, firms, and organizations for ties to Israel, military backgrounds, and industry details.                                
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
                                (currently in development - Some features and data may not yet be available)<br/>
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                                Look up people, venture capital firms, angel investors, or organizations. Available information includes:
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                <Typography component="li" variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                                    Headquarters or operations in Israel
                                </Typography>
                                <Typography component="li" variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                                    Ties to Unit 8200 (Israeli military intelligence) or Military background
                                </Typography>
                                <Typography component="li" variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                                    Industry focus
                                </Typography>
                                <Typography component="li" variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>
                                    Brief descriptions of the person, firm, or company
                                </Typography>
                            </Box>
                        </Card>
                        {isFinancialDataEnabled && (
                            <Card
                                elevation={2}
                                sx={{
                                    p: 3,
                                    cursor: 'pointer',
                                    transition: 'box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: 4
                                    }
                                }}
                                onClick={handleFinancialDataClick}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <TrendingUp sx={{ color: 'success.main', mr: 2 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                                        Financial Data:
                                    </Typography>
                                </Box>
                                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                    Explore how universities and endowments invest through private equity and venture capital.
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 3 }}>
                                    Search universities or endowments to explore their financial activity. Each profile includes:
                                </Typography>
                                <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                    <Typography component="li" variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                                        <strong>Fund Portfolio</strong> – Private equity funds invested in, and the fund managers involved.
                                    </Typography>
                                    <Typography component="li" variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                                        <strong>Buyout Deals</strong> – Companies acquired through private equity deals.
                                    </Typography>
                                    <Typography component="li" variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                                        <strong>Venture Capital Deals</strong> – Organizations financed through venture capital investments.
                                    </Typography>
                                </Box>
                            </Card>
                        )}
                    </Box>
                </Box>

            </Box>
            <DisclaimerModal 
                open={disclaimerOpen} 
                onClose={closeDisclaimer} 
            />
        </PageContainer>
    );
};

export default AboutUs;